// Approval-rule resolution service — the single source of truth consumed by:
//   • Admin "Approval Rules" config (list / edit / preview / publish)
//   • Requester "Approval Route Preview" on the Order Request form
//   • Manager PR / Order approval routing
//
// Rules are held in an in-memory store seeded with typed mock data. Publishing
// freezes an immutable version; in-flight requests keep the version they were
// created against (callers pass a pinned version when they have one).
//
// TODO: replace this in-memory store + resolver with the real endpoints
// (GET/PUT /private/procurement/approval-rules, POST .../resolve). The exported
// function signatures are intended to map 1:1 onto those endpoints.

const APPROVERS = [
  { value: "u-center-admin", label: "Center Admin", type: "user" },
  { value: "u-finance-lead", label: "Finance Lead", type: "user" },
  { value: "u-proc-manager", label: "Procurement Manager", type: "user" },
  { value: "g-finance", label: "Finance Team", type: "group" },
  { value: "u-cfo", label: "CFO", type: "user" },
]

export const getApproverDirectory = () => APPROVERS
const approverLabel = (v) => APPROVERS.find((a) => a.value === v)?.label || v

let RULES = [
  {
    id: 1,
    name: "Standard purchases (≤ 50M)",
    entityType: "order",
    category: "",
    region: "",
    isActive: true,
    isDraft: false,
    version: 2,
    ruleGroupId: null,
    publishedAt: "2026-05-10T10:00:00Z",
    createdAt: "2026-05-01T08:00:00Z",
    updatedAt: "2026-05-12T10:30:00Z",
    conditions: [
      { field: "amount", operator: "<=", value: "50000000", valueType: "NUMBER" }
    ],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null },
      { stepOrder: 2, approverType: "user", approverRef: "u-proc-manager", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null }
    ],
  },
  {
    id: 2,
    name: "High-value purchases (> 50M)",
    entityType: "order",
    category: "",
    region: "",
    isActive: true,
    isDraft: false,
    version: 1,
    ruleGroupId: null,
    publishedAt: "2026-04-01T09:00:00Z",
    createdAt: "2026-03-20T08:00:00Z",
    updatedAt: "2026-04-01T09:15:00Z",
    conditions: [
      { field: "amount", operator: ">=", value: "50000001", valueType: "NUMBER" }
    ],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null },
      { stepOrder: 2, approverType: "user", approverRef: "u-proc-manager", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null },
      { stepOrder: 3, approverType: "user", approverRef: "u-finance-lead", mode: "sequential", quorum: 1, slaHours: 48, escalateTo: "u-proc-manager" },
      { stepOrder: 4, approverType: "user", approverRef: "u-cfo", mode: "sequential", quorum: 1, slaHours: 72, escalateTo: "u-finance-lead" }
    ],
  },
  {
    id: 3,
    name: "IT Equipment (all amounts)",
    entityType: "purchase",
    category: "it_equipment",
    region: "",
    isActive: true,
    isDraft: false,
    version: 3,
    ruleGroupId: null,
    publishedAt: "2026-06-22T09:00:00Z",
    createdAt: "2026-06-15T08:00:00Z",
    updatedAt: "2026-06-22T09:00:00Z",
    conditions: [
      { field: "category", operator: "=", value: "it_equipment", valueType: "STRING" }
    ],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null },
      { stepOrder: 2, approverType: "group", approverRef: "g-finance", mode: "parallel", quorum: 2, slaHours: 24, escalateTo: null }
    ],
  },
  {
    id: 4,
    name: "Marketing & Events (new — never published)",
    entityType: "purchase",
    category: "marketing",
    region: "",
    isActive: false,
    isDraft: true,
    version: 0,
    ruleGroupId: null,
    publishedAt: null,
    createdAt: "2026-07-28T08:00:00Z",
    updatedAt: "2026-07-28T08:00:00Z",
    conditions: [],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null }
    ],
  },
  {
    id: 5,
    name: "South region payments",
    entityType: "payment",
    category: "",
    region: "South",
    isActive: true,
    isDraft: false,
    version: 1,
    ruleGroupId: null,
    publishedAt: "2026-06-01T09:00:00Z",
    createdAt: "2026-05-20T08:00:00Z",
    updatedAt: "2026-06-01T09:00:00Z",
    conditions: [
      { field: "region", operator: "=", value: "South", valueType: "STRING" },
      { field: "amount", operator: ">=", value: "10000000", valueType: "NUMBER" }
    ],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-proc-manager", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null },
      { stepOrder: 2, approverType: "user", approverRef: "u-finance-lead", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: "u-cfo" },
      { stepOrder: 3, approverType: "user", approverRef: "u-cfo", mode: "sequential", quorum: 1, slaHours: 48, escalateTo: null }
    ],
  },
  {
    id: 6,
    name: "Facilities urgent requests (editing v1 — unpublished changes)",
    entityType: "order",
    category: "facilities",
    region: "Central",
    isActive: true,
    isDraft: true,
    version: 1,
    ruleGroupId: 6,
    publishedAt: "2026-07-01T09:00:00Z",
    createdAt: "2026-06-25T08:00:00Z",
    updatedAt: "2026-08-05T11:20:00Z",
    conditions: [
      { field: "urgency", operator: "=", value: "urgent", valueType: "STRING" }
    ],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "parallel", quorum: 2, slaHours: 12, escalateTo: null },
      { stepOrder: 2, approverType: "group", approverRef: "g-finance", mode: "parallel", quorum: 2, slaHours: 12, escalateTo: null }
    ],
  },
  {
    id: 7,
    name: "Professional services (disabled)",
    entityType: "purchase",
    category: "services",
    region: "",
    isActive: false,
    isDraft: false,
    version: 2,
    ruleGroupId: null,
    publishedAt: "2026-03-15T09:00:00Z",
    createdAt: "2026-03-01T08:00:00Z",
    updatedAt: "2026-07-10T16:00:00Z",
    conditions: [],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null },
      { stepOrder: 2, approverType: "user", approverRef: "u-proc-manager", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null }
    ],
  },
  {
    id: 8,
    name: "Teaching materials (catch-all, no conditions)",
    entityType: "order",
    category: "teaching_materials",
    region: "",
    isActive: true,
    isDraft: false,
    version: 1,
    ruleGroupId: null,
    publishedAt: "2026-02-10T09:00:00Z",
    createdAt: "2026-02-01T08:00:00Z",
    updatedAt: "2026-02-10T09:00:00Z",
    conditions: [],
    steps: [
      { stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null }
    ],
  },
]

const clone = (r) => JSON.parse(JSON.stringify(r))

export const listRules = (filters = {}) => {
  let out = RULES.map((r) => {
    const { conditions, steps, ruleGroupId, publishedAt, createdAt, ...listFields } = r
    return {
      ...listFields,
      stepsCount: r.steps.length,
    }
  })
  if (filters.entity) out = out.filter((r) => r.entityType === filters.entity)
  if (filters.category) out = out.filter((r) => r.category === filters.category)
  if (filters.region) out = out.filter((r) => r.region === filters.region)
  return out
}

export const getRule = (id) => {
  const r = RULES.find((x) => x.id === id)
  if (!r) return null
  const { conditions, steps, ...ruleFields } = r
  return {
    rule: ruleFields,
    conditions: clone(conditions),
    steps: clone(steps),
    version: r.version,
    isDraft: r.isDraft,
  }
}

export const saveRule = (rule) => {
  const now = new Date().toISOString()
  const ruleData = rule.rule || rule
  const ruleId = ruleData.id

  if (ruleId && RULES.some((r) => r.id === ruleId)) {
    RULES = RULES.map((r) => {
      if (r.id !== ruleId) return r
      return {
        ...r,
        name: ruleData.name || r.name,
        entityType: ruleData.entityType || r.entityType,
        category: ruleData.category !== undefined ? ruleData.category : r.category,
        region: ruleData.region !== undefined ? ruleData.region : r.region,
        conditions: rule.conditions || r.conditions,
        steps: rule.steps || r.steps,
        updatedAt: now,
      }
    })
    return getRule(ruleId)
  }

  const newId = Math.max(...RULES.map(r => r.id), 0) + 1
  const created = {
    id: newId,
    name: ruleData.name || "",
    entityType: ruleData.entityType || "order",
    category: ruleData.category || "",
    region: ruleData.region || "",
    isActive: false,
    isDraft: true,
    version: 0,
    ruleGroupId: null,
    publishedAt: null,
    createdAt: now,
    updatedAt: now,
    conditions: rule.conditions || [],
    steps: rule.steps || [],
  }
  RULES = [...RULES, created]
  return getRule(created.id)
}

export const publishRule = (id) => {
  const now = new Date().toISOString()
  let published = null
  RULES = RULES.map((r) => {
    if (r.id !== id) return r
    published = {
      ...r,
      isActive: true,
      isDraft: false,
      version: (r.version || 0) + 1,
      publishedAt: now,
      updatedAt: now,
    }
    return published
  })
  return published ? { id: published.id, version: published.version, publishedAt: published.publishedAt } : null
}

const conditionMatches = (cond, ctx) => {
  const { field, operator, value } = cond
  const ctxValue = ctx[field]

  if (field === "amount") {
    const amount = Number(ctxValue) || 0
    const threshold = Number(value) || 0
    switch (operator) {
      case ">=":
      case "gte":
        return amount >= threshold
      case "<=":
      case "lte":
        return amount <= threshold
      case ">":
      case "gt":
        return amount > threshold
      case "<":
      case "lt":
        return amount < threshold
      default:
        return true
    }
  }

  if (field === "category") {
    return !ctxValue || ctxValue === value
  }
  if (field === "region") {
    return !ctxValue || ctxValue === value
  }
  if (field === "center") {
    return !ctxValue || ctxValue === value
  }
  if (field === "department") {
    return !ctxValue || ctxValue === value
  }
  if (field === "urgency") {
    return !ctxValue || ctxValue === value
  }

  return true
}

export const resolveApprovalChain = (ctx = {}) => {
  const entity = ctx.entityType || ctx.entity || "order"
  const candidates = RULES.filter(
    (r) => r.isActive && !r.isDraft && r.entityType === entity
  )

  let best = null
  let bestScore = -1
  candidates.forEach((rule) => {
    const allMatch = rule.conditions.every((c) => conditionMatches(c, ctx))
    if (!allMatch) return
    const score = rule.conditions.length
    if (score > bestScore) {
      best = rule
      bestScore = score
    }
  })

  const matched = best
    ? best
    : {
        id: null,
        name: "Default route",
        version: 0,
        steps: [{ stepOrder: 1, approverType: "user", approverRef: "u-center-admin", mode: "sequential", quorum: 1, slaHours: 24, escalateTo: null }],
      }

  return {
    matchedRuleId: matched.id,
    ruleName: matched.name,
    version: matched.version,
    steps: matched.steps.map((s) => ({
      stepOrder: s.stepOrder,
      approverType: s.approverType,
      approverRef: s.approverRef,
      approverLabel: approverLabel(s.approverRef),
      mode: s.mode,
      quorum: s.quorum,
      slaHours: s.slaHours,
      escalateTo: s.escalateTo,
    })),
  }
}

export const makeEmptyRule = () => ({
  rule: {
    id: null,
    name: "",
    entityType: "order",
    category: "",
    region: "",
    isActive: false,
    isDraft: true,
    version: 0,
    ruleGroupId: null,
    publishedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    notifierEmails: "",
  },
  conditions: [],
  steps: [],
  version: 0,
  isDraft: true,
})
